import { createReducer } from "reduxsauce"
import { takeLatest, select, put } from "redux-saga/effects"
import actionCreator from "helpers/actionCreator"
import { getDisabled } from "./selectors"
import { getCategoryItemsSubset } from "./selectors/data"
import { get, concat, includes, pick, without, forEach, findIndex, toLower } from "lodash"
import { createDefaultReducer } from "helpers/reducers"

export const { Types, Creators } = actionCreator("ompnt/modules/analyzeCompositeRisk", {
  toggle: {
    props: ["factorId", "showSegment", "items"],
    trackType: "composite-analyze-risk-graph-legend-factor",
    trackPayload: ({ factorId, currentState }) => ({ factor: factorId, state: currentState ? "on" : "off" }),
  },
  toggleOnly: {
    props: ["factorId", "only", "showSegment", "items"],
    trackType: "composite-analyze-risk-graph-legend-factor",
    trackPayload: ({ factorId, state }) => ({ factor: factorId, state }),
  },
  focusChartLine: {
    props: ["focusedChartLine"],
    track: false,
  },
  blurChartLine: { track: false },
  addSelectedView: {
    props: ["factorId"],
    trackType: "composite-analyze-trends-select-view-factor-add",
  },
  removeSelectedView: {
    props: ["factorId", "options"],
    trackType: "composite-analyze-trends-select-view-factor-removal",
    trackPayload: props => pick(props, "factorId"),
  },
  selectFactorCategory: {
    props: ["factorId"],
    trackType: "composite-analyze-risk-category-selection",
    trackPayload: ({ factorId }) => ({ type: factorId }),
  },
}, { track: true })

const DEFAULT_STATE = {
  focusedChartLine: null,
}

const resetGraphState = (state) => ({
  ...state,
  disabled: null,
  only: null,
})

function handleToggle(state, { factorId }) {
  const disabled = getDisabled()
  return { ...state, focusedChartLine: !includes(disabled, factorId) ? factorId : null }
}

export default createReducer(DEFAULT_STATE, {
  [Types.FOCUS_CHART_LINE]: createDefaultReducer(),
  [Types.BLUR_CHART_LINE]: createDefaultReducer({ customStateValues: { focusedChartLine: null } }),
  [Types.TOGGLE]: handleToggle,
})

const handleSelectFactorCategory = (state, { factorId }) => {
  const newState = { factorCategory: factorId }
  const disabled = get(state, "disabled", [])
  if (factorId !== "total" && factorId !== "factors" && !includes(disabled, `${factorId}_total`)) {
    newState.disabled = concat(disabled, [`${factorId}_total`])
  }

  return { ...resetGraphState(state), ...newState }
}

const handleToggleQS = (state, { factorId, showSegment }) => {
  let disabled = get(state, "disabled", [])
  const currentState = !includes(disabled, factorId)
  if (!currentState) {
    if (!showSegment && (factorId === "specific" || factorId === "factor")) {
      disabled = without(disabled, "specific", "factor")
    } else {
      disabled = without(disabled, factorId)
    }
  } else if (!showSegment && (factorId === "specific" || factorId === "factor")) {
    disabled = [...disabled, "specific", "factor"]
  } else {
    disabled = [...disabled, factorId]
  }

  return {
    ...state,
    only: null,
    disabled,
  }
}

const handleToggleOnlyQS = (state, { factorId, only, showSegment, items }) => {
  const disabled = []
  let onlyDisplay = null

  if (toLower(only) === "only") {
    onlyDisplay = factorId

    if (!showSegment && (factorId === "specific" || factorId === "factor")) {
      items.forEach(item => {
        if (item.id !== factorId && item.id !== "specific" && item.id !== "factor") {
          disabled.push(item.id)
        }
      })
    } else {
      forEach(items, item => {
        if (item.id !== factorId) {
          disabled.push(item.id)
        }
      })
    }
  }

  return { ...state, disabled, only: onlyDisplay }
}

const handleAddSelectedView = (state, { factorId }) => ({
  ...state,
  views: [...(state.views || []), factorId],
  factorCategory: factorId,
})

const handleRemoveSelectedView = (state, { factorId, options }) => {
  let { factorCategory } = state
  if (factorCategory === factorId) {
    const optionIndex = findIndex(options, option => option.id === factorId)
    factorCategory = get(options, `${optionIndex - 1}.id`)
  }

  return {
    ...state,
    factorCategory,
    views: without(state.views, factorId),
  }
}

export const queryStringReducers = {
  [Types.SELECT_FACTOR_CATEGORY]: handleSelectFactorCategory,
  [Types.TOGGLE]: handleToggleQS,
  [Types.TOGGLE_ONLY]: handleToggleOnlyQS,
  [Types.ADD_SELECTED_VIEW]: handleAddSelectedView,
  [Types.REMOVE_SELECTED_VIEW]: handleRemoveSelectedView,
}

function* handleToggleOnly({ id, showSegment }) {
  const { items, only } = yield select(state => ({
    items: getCategoryItemsSubset(state),
    only: state.modules.analyzeRisk.only,
  }))

  yield put(Creators.toggleOnlySuccess(id, only !== id, items, showSegment))
}

export function* saga() {
  yield takeLatest(Types.TOGGLE_ONLY, handleToggleOnly)
}
